<script setup lang="ts">
import type { GenericParameters } from "~/core/models/GenericParameters.type";
import type { UseFetchResponse } from "~/core/models/UseFetchResponse.interface";
import { useDelegationUpload } from "~/modules/relation/delegation/composables/useDelegationUpload";
import delegationService from "~/modules/relation/delegation/services/delegation.service";
import type { CreateRelationByUploadingIdCardPayload } from "~/modules/relation/models/CreateRelationByUploadingIdCardPayload.interface";
import type { RelationEntity } from "~/modules/relation/models/RelationEntity.interface";

const props = defineProps<
  GenericParameters & {
    fetchGivenDelegationResponse?: UseFetchResponse<RelationEntity>;
  }
>();

const {
  isLoading: isLoadingUploadIdCard,
  error: errorUploadIdCard,
  showUploadIdCardModal,
  canCreateDelegationByUploadingIdCard,
  uploadIdCard,
} = useDelegationUpload(props);

const showCreateOrganizationSideCanvas = ref(false);

async function createDelegationByUploadingIdCard(
  $event: CreateRelationByUploadingIdCardPayload,
) {
  await uploadIdCard($event);
  if (!errorUploadIdCard.value) {
    props.fetchGivenDelegationResponse?.search();
  }
}

function getDeleteFn(targetOrganizationCode: number) {
  return async () => {
    await delegationService.deleteOrganizationGivenDelegation({
      organizationCode: props.organizationCode,
      targetOrganizationCode,
    });
  };
}
</script>

<template>
  <div class="w-full flex flex-col gap-4">
    <relation-add-bar
      v-model:show-upload-id-card-modal="showUploadIdCardModal"
      context="delegation"
      :is-loading="isLoadingUploadIdCard"
      :disabled="!canCreateDelegationByUploadingIdCard()"
      @upload-id-card="createDelegationByUploadingIdCard"
      @create-organization="showCreateOrganizationSideCanvas = true"
    />

    <relation-entity-list
      from-to="to"
      :pagination="
        props.fetchGivenDelegationResponse?.results?.value?.pagination
      "
      :relations="props.fetchGivenDelegationResponse?.results?.value?.results"
      :is-loading="props.fetchGivenDelegationResponse?.isLoading.value"
      :error="props.fetchGivenDelegationResponse?.error.value"
      :delete-fn="getDeleteFn"
      @change-page-and-page-size="props.fetchGivenDelegationResponse?.fetchData"
      @delete-success="props.fetchGivenDelegationResponse?.fetchData"
    />
  </div>
  <organization-create
    v-if="businessUnitCode"
    v-model:show="showCreateOrganizationSideCanvas"
    :business-unit-code="businessUnitCode"
    create-delegation
    :target-organization-code="organizationCode"
    @create:success="props.fetchGivenDelegationResponse?.fetchData"
  />
</template>
